// Product gallery: the selected variant's image first, then the images shared by all variants.
// Desktop keeps the first image fixed beside a slider with thumbnails; phones — and any gallery
// marked compact, such as the one in the quick-add drawer — show a single slider.
// A custom element rather than an inline script, so it also runs where the markup is injected.
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.sliderEl = this.querySelector('.swiper');
    this.wrapper = this.querySelector('.swiper-wrapper');
    this.fixedEl = this.querySelector('.cstm-product-images__fixed');
    this.thumbsEl = this.querySelector('.cstm-product-images__thumbs');
    if (!this.sliderEl || !this.wrapper) return;

    this.slides = Array.from(this.wrapper.children);
    this.thumbs = Array.from(this.thumbsEl.children);
    this.compact = this.hasAttribute('data-compact');
    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.swiper = null;
    this.visibleSlides = [];
    this.currentMediaId = this.dataset.currentMediaId || null;

    this.onBreakpoint = () => this.render(this.currentMediaId);
    this.onVariantChange = (event) => {
      this.currentMediaId = event.detail.variant?.featured_media?.id;
      this.render(this.currentMediaId);
    };
    this.onThumbClick = (event) => {
      const thumb = event.target.closest('.cstm-product-images__thumb');
      if (!thumb || !this.swiper) return;
      this.swiper.slideTo(Array.from(this.thumbsEl.children).indexOf(thumb));
    };

    this.render(this.currentMediaId);
    this.classList.add('is-ready');
    this.revealLoadedImages();

    // The sticky offsets in CSS need the gallery's and the fixed image's own heights.
    this.resizeObserver = new ResizeObserver(() => {
      this.style.setProperty('--gallery-height', `${this.offsetHeight}px`);
      this.style.setProperty('--fixed-height', `${this.fixedEl.offsetHeight}px`);
    });
    this.resizeObserver.observe(this);

    this.thumbsEl.addEventListener('click', this.onThumbClick);
    this.desktopQuery.addEventListener('change', this.onBreakpoint);
    document.addEventListener('change:variant', this.onVariantChange);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.thumbsEl?.removeEventListener('click', this.onThumbClick);
    this.desktopQuery?.removeEventListener('change', this.onBreakpoint);
    document.removeEventListener('change:variant', this.onVariantChange);
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  get isDesktop() {
    return this.desktopQuery.matches && !this.compact;
  }

  // Other variants' images stay detached, so the grid and Swiper only see what is shown.
  selectSlides(mediaId) {
    const byPosition = (a, b) => a.dataset.position - b.dataset.position;
    const variantSlide = this.slides.find((slide) => slide.dataset.imageId === String(mediaId));
    const sharedSlides = this.slides.filter((slide) => slide.dataset.variantMedia === 'false').sort(byPosition);
    const selected = variantSlide ? [variantSlide, ...sharedSlides] : sharedSlides;

    this.visibleSlides = selected.length ? selected : [...this.slides].sort(byPosition);
    this.visibleSlides.forEach((slide) => slide.removeAttribute('hidden'));
  }

  placeSlides() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.querySelector('.swiper-pagination')?.replaceChildren();

    if (this.isDesktop) {
      const rest = this.visibleSlides.slice(1);
      this.fixedEl.replaceChildren(...this.visibleSlides.slice(0, 1));
      this.wrapper.replaceChildren(...rest);
      this.classList.toggle('is-single', rest.length === 0);

      const restThumbs = rest.length > 1
        ? rest.map((slide) => this.thumbs.find((thumb) => thumb.dataset.imageId === slide.dataset.imageId))
        : [];
      this.thumbsEl.replaceChildren(...restThumbs);
    } else {
      this.classList.remove('is-single');
      this.fixedEl.replaceChildren();
      this.wrapper.replaceChildren(...this.visibleSlides);
      this.thumbsEl.replaceChildren();
    }
  }

  updateThumbs() {
    Array.from(this.thumbsEl.children).forEach((thumb, index) => {
      const isActive = this.swiper && index === this.swiper.activeIndex;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-current', isActive);
    });
  }

  initSwiper() {
    if (!window.Swiper || !this.wrapper.children.length) return;

    const navigation = {
      nextEl: this.querySelector('.swiper-button-next'),
      prevEl: this.querySelector('.swiper-button-prev'),
    };

    if (this.isDesktop) {
      this.swiper = new window.Swiper(this.sliderEl, {
        navigation,
        slidesPerView: 1,
        rewind: true,
        on: { slideChange: () => this.updateThumbs() },
      });
      this.updateThumbs();
    } else {
      this.swiper = new window.Swiper(this.sliderEl, {
        navigation,
        pagination: {
          el: this.querySelector('.swiper-pagination'),
          clickable: true,
        },
        // The product page shows one full-width image at a time; the compact gallery (quick-add drawer)
        // keeps a peek of the next one.
        slidesPerView: this.hasAttribute('data-compact') && this.wrapper.children.length > 1 ? 1.1 : 1,
        spaceBetween: 8,
      });
    }
  }

  render(mediaId) {
    this.selectSlides(mediaId);
    this.placeSlides();
    this.initSwiper();
  }

  // Broadcast keeps images hidden until theme.dev.js removes is-loading, which lags far behind the
  // download. Reveal gallery images the moment they load, the same way the theme does.
  revealLoadedImages() {
    this.slides.forEach((slide) => {
      slide.querySelectorAll('img.is-loading').forEach((img) => {
        const reveal = () => {
          [img, img.parentNode, img.parentNode.parentNode].forEach((el) => el.classList.remove('is-loading'));
        };
        if (img.complete && img.naturalWidth) {
          reveal();
        } else {
          img.addEventListener('load', reveal, { once: true });
        }
      });
    });
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
